import React from 'react';

import ShieldCheckSolidIcon from '../../../../../../shared/icons/shield-check-solid-icon';
import Button from '../../../../../button/button';
import { InputError } from '../../../../../input/input-types';
import Select from '../../../../../select/select';
import Text from '../../../../../text/text';
import Tooltip from '../../../../../tooltip/tooltip';
import Typography from '../../../../../typography/typography';
import { EVENTS } from '../../../../fitment-constants';

import { OptionValue } from '../../../../../../shared/types/select';
import {
  CANADA_ISO_CODE,
  COUNTRIES,
  PROVINCES,
  STATES
} from '../../../../../../shared/constants/form';

import './fitment-license-lookup.scss';

interface SelectFieldConfig {
  error?: InputError;
  name: string;
  value: OptionValue;
}

interface StringFieldConfig {
  error?: InputError;
  name: string;
  value: string;
}

interface OnEventProps {
  name: string;
  payload?: unknown;
}

interface FitmentLicenseLookupProps {
  fields: {
    country: SelectFieldConfig;
    licensePlate: StringFieldConfig;
    state: SelectFieldConfig;
  };
  onEvent: ({ name, payload }: OnEventProps) => void;
  onFocusLicensePlateDropdown?: () => void;
  onSubmitLicensePlate: () => void;
  setCountry: (value: OptionValue) => void;
  shouldAutofocusLicensePlate: boolean;
  updateField: (name: string, value: OptionValue | string) => void;
}

function FitmentLicenseLookup(props: FitmentLicenseLookupProps) {
  const {
    fields,
    onEvent,
    onFocusLicensePlateDropdown,
    onSubmitLicensePlate,
    setCountry,
    shouldAutofocusLicensePlate,
    updateField
  } = props;

  const { country, licensePlate, state } = fields;

  return (
    <div styleName="container">
      <Typography styleName="or" variant="H6" weight="black">
        OR
      </Typography>
      <Typography styleName="info-text" variant="H6" weight="black">
        Skip steps by entering your Vehicle license plate{' '}
        <Tooltip
          content="Custom vanity plates or newer vehicles may not be found. If you're having trouble with the license plate lookup, please enter your vehicle's year, make and model to continue."
          onClick={() =>
            onEvent?.({
              name: EVENTS.VEHICLE_FITMENT_LP_NEED_HELP
            })
          }
          styleName="tooltip"
        />
      </Typography>
      <Typography styleName="disclaimer">
        <ShieldCheckSolidIcon styleName="icon" />
        This data is protected and will only be used to detect your vehicle
        model
      </Typography>
      <Text
        label="Your vehicle license plate"
        name={licensePlate.name}
        onBlur={e => updateField(licensePlate.name, e.target.value)}
        onChange={e => updateField(licensePlate.name, e.target.value)}
        onFocus={onFocusLicensePlateDropdown}
        shouldAutoFocus={shouldAutofocusLicensePlate}
        styleName={!licensePlate?.value ? 'license-plate-select' : 'select'}
        value={licensePlate?.value?.toUpperCase()}
      />
      {licensePlate?.value && (
        <>
          <Select
            data-testid="fitment-license-lookup__country"
            isRequired
            isSearchable
            name={country.name}
            onChange={value => setCountry(value)}
            options={COUNTRIES}
            placeholder="Country"
            styleName="select"
            value={country.value}
          />
          <Select
            data-testid="fitment-license-lookup__state"
            error={state.error}
            isDisabled={!country.value}
            isRequired
            isSearchable
            name={state.name}
            onChange={value => updateField(state.name, value)}
            options={
              country?.value?.value === CANADA_ISO_CODE ? PROVINCES : STATES
            }
            placeholder="State"
            styleName="select"
            value={state.value}
          />
          <Button
            data-testid="fitment-license-lookup__submit-btn"
            isDisabled={!(licensePlate.value && state.value && country.value)}
            isPrimary
            onClick={onSubmitLicensePlate}
            styleName="submit-btn"
          >
            Submit
          </Button>
        </>
      )}
    </div>
  );
}

export default FitmentLicenseLookup;
